import { storeLogger, EVENTS } from './logging';
import { POSITIONS_TTL_MS } from '@/providers/wealthica2/utils/query-config';

type UnknownRecord = Record<string, unknown>;

export const createPositionStateActions = (set: unknown, get: unknown) => {
  const _set = set as (updater: (state: { connections: Map<string, unknown> }) => Partial<{ connections: Map<string, unknown> }>) => void;
  const _get = get as () => { connections: Map<string, unknown> };
  return ({
  // === POSITION MANAGEMENT ===
  setPositionsForInstitution: (institutionId: string, positions: unknown[]) => {
    // Deduplicate positions by ID to prevent duplicate data
    const uniquePositions = positions.filter((position, index, array) => {
      const rec = position as UnknownRecord;
      const positionId = (rec['_id'] as string | undefined) || (rec['id'] as string | undefined);
      if (!positionId) return true; // Keep positions without IDs
      // Keep only the first occurrence of each position ID
      return array.findIndex((p) => {
        const pr = p as UnknownRecord;
        const pid = (pr['_id'] as string | undefined) || (pr['id'] as string | undefined);
        return pid === positionId;
      }) === index;
    });

    const duplicatesRemoved = positions.length - uniquePositions.length;
    if (duplicatesRemoved > 0) {
      console.log('🔧 MULTI-STORE: Removed duplicate positions', {
        institutionId,
        duplicatesRemoved
      });
    }

    _set((state: { connections: Map<string, unknown> }) => {
      const connection = state.connections.get(institutionId) as UnknownRecord | undefined;
      if (!connection) return state;

      const newConnections = new Map(state.connections);
      newConnections.set(institutionId, {
        ...(connection as UnknownRecord),
        positions: uniquePositions,
        lastPositionsFetch: new Date(),
        isLoadingPositions: false,
        positionsError: null
      });

      return { connections: newConnections };
    });

    // CRITICAL FIX: Cache positions for this institution to create per-institution storage keys
    if (uniquePositions.length > 0) {
      const cacheablePositions = uniquePositions.filter((p) => typeof (p as UnknownRecord)['id'] === 'string') as Array<{ id: string } & UnknownRecord>;
      import('../../providers/wealthica2/utils/cache-utils').then(({ setCachedPositions, getPositionsCacheKey }) => {
        // setCachedPositions expects Position[] or AccountPositionsMap; we supply Position[]-compatible objects
        setCachedPositions(institutionId, cacheablePositions as unknown as Array<{ id: string }>);
        const cacheKey = getPositionsCacheKey(institutionId);
        storeLogger.info('multiWealthica2Store', EVENTS.POSITIONS_CACHED, {
          institutionId,
          positionCount: cacheablePositions.length,
          cacheKey
        });
      }).catch((error) => {
        storeLogger.warn('multiWealthica2Store', EVENTS.POSITIONS_CACHED, {
          phase: 'failed',
          institutionId,
          error
        });
      });
    }

    // Notify listeners that positions have been loaded for this institution (including zero)
    try {
      window.dispatchEvent(new CustomEvent('wealthica:positions:loaded', {
        detail: { institutionId, count: uniquePositions.length }
      }));
    } catch { /* noop */ }
  },

  setPositionsLoadingForInstitution: (institutionId: string, loading: boolean) => {
    _set((state: { connections: Map<string, unknown> }) => {
      const connection = state.connections.get(institutionId) as UnknownRecord | undefined;
      if (!connection) return state;

      const newConnections = new Map(state.connections);
      newConnections.set(institutionId, {
        ...(connection as UnknownRecord),
        isLoadingPositions: loading
      });

      return { connections: newConnections };
    });
  },

  setPositionsErrorForInstitution: (institutionId: string, error: string | null) => {
    _set((state: { connections: Map<string, unknown> }) => {
      const connection = state.connections.get(institutionId) as UnknownRecord | undefined;
      if (!connection) return state;

      const newConnections = new Map(state.connections);
      newConnections.set(institutionId, {
        ...(connection as UnknownRecord),
        positionsError: error,
        isLoadingPositions: false
      });

      return { connections: newConnections };
    });
  },

  clearPositionsForInstitution: (institutionId: string) => {
    ( (_get() as unknown) as { setPositionsForInstitution: (id: string, positions: unknown[]) => void } ).setPositionsForInstitution(institutionId, []);
  },

  // Centralized position fetching - delegates to action in multi-account-actions
  fetchPositionsForInstitution: async (institutionId: string) => {
    const state = _get();
    const connection = state.connections.get(institutionId) as UnknownRecord | undefined;

    if (!connection) {
      console.warn('🚫 MULTI-STORE: Cannot fetch positions - connection not found', { institutionId });
      return;
    }

    // TTL CACHE: Skip fetch if we fetched positions within TTL and positions are present
    try {
      const lastFetchRaw = (connection as UnknownRecord)['lastPositionsFetch'] as Date | string | null | undefined;
      const lastFetch = lastFetchRaw instanceof Date ? lastFetchRaw : (lastFetchRaw ? new Date(String(lastFetchRaw)) : null);
      const positionsArr = ((connection as UnknownRecord)['positions'] as unknown[]) || [];
      const cacheWindowMs = POSITIONS_TTL_MS; // 90 minutes
      if (lastFetch && positionsArr.length > 0 && (Date.now() - lastFetch.getTime()) < cacheWindowMs) {
        console.log('🟡 MULTI-STORE: Skipping positions fetch (recently fetched)', {
          institutionId,
          lastFetch: lastFetch.toISOString(),
          ageMs: Date.now() - lastFetch.getTime(),
          positionsCount: positionsArr.length
        });
        return;
      }
    } catch { /* noop */ }

    // Prevent concurrent fetches for same institution
    if ((connection as UnknownRecord)['isLoadingPositions']) {
      console.log('🔄 MULTI-STORE: Position fetch already in progress for institution', { institutionId });
      return;
    }

    // Global guard: skip for cash-only institutions with no positions hints
    // BUT always perform the FIRST EVER fetch per institution to be authoritative
    try {
      const accounts = ((connection as UnknownRecord)['accounts'] as unknown[]) || [];
      if (accounts.length > 0) {
        const anyPositionsHint = accounts.some((a) => {
          const aRec = a as UnknownRecord;
          const poss = (aRec['positions'] as unknown[]) || [];
          return Array.isArray(poss) && poss.length > 0;
        });
        const allCashish = accounts.every((a) => {
          const aRec = a as UnknownRecord;
          const classification = (aRec['classification'] as UnknownRecord | undefined);
          const cls = String((classification?.['cdn_asset_class'] as string | undefined) ?? '').toLowerCase();
          const t = String((aRec['type'] as string | undefined) ?? '').toLowerCase();
          return cls === 'cash' || t.includes('cash');
        });
        const lastFetchRaw2 = (connection as UnknownRecord)['lastPositionsFetch'] as Date | string | null | undefined;
        const lastFetch2 = lastFetchRaw2 instanceof Date ? lastFetchRaw2 : (lastFetchRaw2 ? new Date(String(lastFetchRaw2)) : null);
        const firstEverFetch = !lastFetch2;
        if (!firstEverFetch && !anyPositionsHint && allCashish) {
          console.log('⏭️ MULTI-STORE: Skipping positions fetch for cash-only institution (no holdings detected)', {
            institutionId,
            accounts: accounts.length
          });
          return;
        }
      }
    } catch (guardErr) {
      console.warn('⚠️ MULTI-STORE: Skip-guard evaluation failed, proceeding with fetch', { institutionId, guardErr });
    }

    try {
      console.log('📈 MULTI-STORE: Fetching positions for institution', { institutionId });
      // Delegate to shared action that uses DataService and handles loading/error states
      const loader = (_get() as unknown) as { loadPositionsForInstitution: (id: string) => Promise<void>; getConnection: (id: string) => unknown };
      await loader.loadPositionsForInstitution(institutionId);
      const after = loader.getConnection(institutionId) as UnknownRecord | undefined;
      console.log('✅ MULTI-STORE: Positions fetched successfully', {
        institutionId,
        positionCount: (((after?.['positions'] as unknown[]) || []).length) || 0
      });
    } catch (error) {
      console.error('❌ MULTI-STORE: Position fetch failed', { institutionId, error });
    }
  },
  });
};
